import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getPlayer, TrackerApiError } from "../api";
import { SearchForm } from "../components/SearchForm";
import { AgentTable, MapTable } from "../components/DataTable";
import type { PlayerResponse } from "../types";

type Tab = "agents" | "maps";

function relativeTime(value: string) {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function PlayerPage() {
  const { gameName = "", tagLine = "" } = useParams();
  const decodedName = decodeURIComponent(gameName);
  const decodedTag = decodeURIComponent(tagLine);
  const navigate = useNavigate();
  const requestKey = `${decodedName}#${decodedTag}`;
  const [request, setRequest] = useState<{ key: string; data: PlayerResponse | null; error: string }>({ key: requestKey, data: null, error: "" });
  const [tab, setTab] = useState<Tab>("agents");

  useEffect(() => {
    const controller = new AbortController();
    getPlayer(decodedName, decodedTag, controller.signal)
      .then((data) => setRequest({ key: requestKey, data, error: "" }))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setRequest({ key: requestKey, data: null, error: caught instanceof TrackerApiError ? caught.message : "Something went wrong while loading this player." });
      });
    return () => controller.abort();
  }, [decodedName, decodedTag, requestKey]);

  const data = request.key === requestKey ? request.data : null;
  const error = request.key === requestKey ? request.error : "";
  const loading = request.key !== requestKey || (!data && !error);

  const title = useMemo(() => data ? `${data.player.gameName}#${data.player.tagLine} — DEFYND VALORANT Tracker` : "DEFYND VALORANT Tracker", [data]);
  useEffect(() => { document.title = title; return () => { document.title = "DEFYND VALORANT Tracker"; }; }, [title]);

  return (
    <main className="player-page">
      <header className="player-nav">
        <Link className="brand" to="/">DEFYND<span>.</span></Link>
        <SearchForm compact initialValue={`${decodedName}#${decodedTag}`} loading={loading} onSearch={(name, tag) => navigate(`/player/${encodeURIComponent(name)}/${encodeURIComponent(tag)}`)} />
        <span className="nav-status"><i /> API ONLINE</span>
      </header>
      {loading && <section className="state-panel"><span className="loader-line" /><p>Pulling competitive record…</p></section>}
      {!loading && error && <section className="state-panel state-panel--error"><p className="kicker">SEARCH FAILED</p><h1>Player unavailable.</h1><p>{error}</p><Link to="/">Return to search</Link></section>}
      {!loading && data && (
        <div className="profile-shell">
          <div className="mode-banner"><span>{data.mode === "demo" ? "DEMO DATA" : "LIVE RIOT DATA"}</span><p>{data.notice}</p></div>
          <section className="profile-header">
            <div className="identity"><p className="section-label">PLAYER PROFILE</p><h1>{data.player.gameName}<span>#{data.player.tagLine}</span></h1><div className="player-meta"><span>LVL {data.player.accountLevel ?? "—"}</span><span>{data.player.region.toUpperCase()}</span><span>{data.player.currentAct ?? "CURRENT ACT"}</span></div></div>
            <div className="rank-block"><div className="rank-emblem"><span>{data.player.rank?.split(" ")[0]?.slice(0, 1) ?? "—"}</span></div><div><p className="section-label">CURRENT RANK</p><strong>{data.player.rank ?? "Unrated"}</strong><span>{data.player.rr == null ? "RR unavailable" : `${data.player.rr} RR`}</span></div></div>
          </section>
          <section className="stats-grid" aria-label="Performance overview">
            {data.stats.map((stat, index) => <article className={index < 4 ? "stat-card stat-card--primary" : "stat-card"} key={stat.label}><p>{stat.label}</p><strong>{stat.value}</strong>{stat.detail && <span>{stat.detail}</span>}</article>)}
          </section>
          <section className="content-section match-section">
            <div className="section-heading"><div><p className="section-label">LAST {data.matches.length} COMPETITIVE</p><h2>Recent matches</h2></div><span>UPDATED {relativeTime(data.generatedAt).toUpperCase()}</span></div>
            <div className="match-list">
              {data.matches.map((match) => <article className={`match-row match-row--${match.result}`} key={match.id}>
                <div className="result-mark"><span>{match.result === "win" ? "W" : match.result === "loss" ? "L" : "D"}</span><small>{match.score}</small></div>
                <div className="agent-cell"><span className="agent-mark">{match.agent.slice(0, 1)}</span><div><strong>{match.agent}</strong><small>{match.map}</small></div></div>
                <div className="match-stat"><span>K / D / A</span><strong>{match.kda}</strong></div>
                <div className="match-stat"><span>ACS</span><strong>{match.acs}</strong></div>
                <div className={`rr ${match.rrChange == null ? "rr--muted" : match.rrChange >= 0 ? "rr--gain" : "rr--loss"}`}>{match.rrChange == null ? "—" : `${match.rrChange > 0 ? "+" : ""}${match.rrChange}`}<span> RR</span></div>
                <div className="match-time"><strong>{relativeTime(match.playedAt)}</strong><span>{match.durationMinutes} min</span></div>
              </article>)}
            </div>
          </section>
          <section className="content-section performance-section">
            <div className="section-heading"><div><p className="section-label">PERFORMANCE BREAKDOWN</p><h2>Where you win</h2></div><div className="tabs"><button className={tab === "agents" ? "active" : ""} onClick={() => setTab("agents")}>Agents</button><button className={tab === "maps" ? "active" : ""} onClick={() => setTab("maps")}>Maps</button></div></div>
            {tab === "agents" ? <AgentTable agents={data.agents} /> : <MapTable maps={data.maps} />}
          </section>
          <footer className="player-footer"><Link className="brand" to="/">DEFYND<span>.</span></Link><p>Statistics are calculated from supported match data. Unsupported metrics are never fabricated.</p></footer>
        </div>
      )}
    </main>
  );
}
